"use client";

import React, { useEffect } from "react";
import { useRouter } from "next/navigation";

import { selectLoginStatus, useAppSelector } from "@repo/global-store";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import { getSSOConfigsFromUrl, ssoRedirect } from "@repo/sso/redirect-utils";
import { isNextPage } from "@repo/shared-utils/common-utils";
import { dataLayer } from "@repo/analytics/data-layer-utils";

/**
 * ClickHandler
 * @returns
 */
export default function ClickHandler() {
  // Get auth status from store if not provided as prop
  const loginStatus = useAppSelector(selectLoginStatus);
  const router = useRouter();

  /**
   * getNearestAnchorElement
   * @param element
   * @returns
   */
  const getNearestAnchorElement = (element: HTMLElement): HTMLAnchorElement | null => {
    if (element.tagName === "A") {
      return element as HTMLAnchorElement;
    }
    const anchorElement = element.closest("a");
    if (anchorElement) {
      return anchorElement;
    }
    return null;
  };

  /**
   * handleAnchorClick
   * @param e
   * @param anchorElement
   */
  const handleHrefClick = async (e: MouseEvent, anchorElement: HTMLAnchorElement) => {
    const href = anchorElement.href.toLowerCase();
    e.preventDefault();
    // Check for Bajaj Super App redirection
    if (href.includes(APP_PROMPTS.BAJAJ_SUPER_APP)) {
      e.preventDefault();
      const windowWithTarsier = window as Window & {
        tarsier?: {
          invokePrompt: (prompt: string, force?: boolean) => unknown;
        };
      };
      windowWithTarsier.tarsier?.invokePrompt(
        `${APP_PROMPTS.IN_APP_REDIRECTION}${anchorElement.href}`,
        true
      );
      return;
    }

    const mapping = getSSOConfigsFromUrl(anchorElement.href);
    const relativeHref = anchorElement.getAttribute("href") ?? anchorElement.href;
    if (mapping?.ssoModule) {
      ssoRedirect(
        {
          href: anchorElement.href,
          target: anchorElement.target,
          checkCart: true,
          showResumePopup: true,
        },
        loginStatus,
        mapping
      );
      return;
    } else {
      try {
        const dlValue = JSON.parse(anchorElement.getAttribute("data-dl") || "{}");
        const ctValue = JSON.parse(anchorElement.getAttribute("data-ct") || "{}");
        dataLayer(loginStatus, dlValue, ctValue);
      } catch (err) {
        console.error("Error parsing ct/dl props:", err);
      }
      if (!isNextPage(relativeHref) || anchorElement.target === "_blank") {
        window.open(anchorElement.href, anchorElement.target);
      } else {
        router.push(anchorElement.href);
      }
    }
  };

  /**
   * addClickListener
   */
  const addClickListener = async () => {
    document?.addEventListener(
      "click",
      (e) => {
        const target = e.target as HTMLElement;
        const anchorElement = getNearestAnchorElement(target);
        if (!anchorElement) return;

        const href = anchorElement.href;
        if (!href) return;

        // Avoid blocking main thread; defer heavy logic
        queueMicrotask(() => handleHrefClick(e, anchorElement));
      },
      { capture: true }
    );
  };

  // Track if the effect has already run
  const hasRunRef = React.useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    if (loginStatus !== "pending") {
      addClickListener();
      hasRunRef.current = true;
    }
  }, [loginStatus]);

  // This component doesn't render anything
  return null;
}
